/**
 * Trusted origins allowed to postMessage to this app.
 *
 * The app handles `window.postMessage` events for the OAuth / iframe-embed flows
 * (e.g. Google Calendar connect, OAuth popup callbacks). To prevent any random
 * framing/opener page from injecting messages (forcing reloads, polluting logs),
 * we only act on messages whose `event.origin` is trusted.
 *
 * HOW TO ADD A NEW EMBED / PARTNER SITE (no code change needed):
 *   1. Runtime (preferred, no rebuild) — set in public/config.js:
 *        window.RUNTIME_CONFIG.TRUSTED_ORIGINS =
 *          "https://app.partner.com,https://*.partner.com";
 *   2. Build time — set the env var (comma-separated):
 *        REACT_APP_TRUSTED_ORIGINS="https://app.partner.com,https://*.partner.com"
 *   3. Permanent default — add it to DEFAULT_TRUSTED_ORIGINS below.
 *
 * Entries may be:
 *   - an exact origin:        "https://app.partner.com"
 *   - a wildcard subdomain:   "https://*.partner.com"  (matches partner.com and any sub)
 *
 * The app's OWN origin is always trusted (OAuth popup/callback pages are same-origin),
 * so it never needs to be listed here.
 */

// Built-in defaults: the Kiln frames the app is embedded in today.
const DEFAULT_TRUSTED_ORIGINS = [
  'https://kiln.com',
  'https://*.kiln.com',
];

// Read extra origins from runtime config first, then the build-time env var.
// Mirrors how environmentConfig.js resolves API_URL (RUNTIME_CONFIG → env → default).
const readConfiguredOrigins = () => {
  const runtime =
    typeof window !== 'undefined' && window.RUNTIME_CONFIG?.TRUSTED_ORIGINS;
  const raw = runtime || process.env.REACT_APP_TRUSTED_ORIGINS || '';
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

// Match an origin against one pattern (exact origin or "https://*.domain.com").
const matchesPattern = (origin, pattern) => {
  if (pattern === origin) return true;

  const wildcard = pattern.match(/^(https?):\/\/\*\.(.+)$/);
  if (!wildcard) return false;

  const [, proto, baseHost] = wildcard;
  try {
    const o = new URL(origin);
    return (
      o.protocol === `${proto}:` &&
      (o.hostname === baseHost || o.hostname.endsWith(`.${baseHost}`))
    );
  } catch {
    return false;
  }
};

/** Full list of configured trusted-origin patterns (defaults + overrides). */
export const getTrustedOrigins = () => [
  ...DEFAULT_TRUSTED_ORIGINS,
  ...readConfiguredOrigins(),
];

/**
 * True if `origin` is allowed to postMessage to the app.
 * Same-origin is always trusted; everything else must match a configured pattern.
 */
export const isTrustedOrigin = (origin) => {
  if (!origin) return false;
  if (typeof window !== 'undefined' && origin === window.location.origin) {
    return true;
  }
  return getTrustedOrigins().some((pattern) => matchesPattern(origin, pattern));
};
